import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";

/**
 * How long a Retry may reuse the same correlation id. The only dedupe on the widget path is the
 * `channel.inbound` consumer's `SET NX PX` claim on the id, which lives 5 minutes; past that a retry
 * of a send the edge DID accept could be delivered twice, so the row stops offering it
 * (chat-experience 11 §6 gap 6).
 */
export const RETRY_WINDOW_MS = 5 * 60 * 1000;

export const isRetryExpired = (sentAt, now = Date.now()) =>
	typeof sentAt === "number" && now - sentAt >= RETRY_WINDOW_MS;

/**
 * A send that did not go through (chat-experience 11, D-20). Presentational: Chat owns the retry.
 * `referenceId` keeps the support affordance the red line had (the correlation id, shown and
 * copyable); Retry is new. Once the retry window has passed only the reference id remains.
 *
 * No `role="alert"`: the failure is announced once by Chat's single polite live region (D-17), and an
 * alert here would make a screen reader say it twice.
 */
export default function SendFailed({ referenceId, sentAt, onRetry, onCopyRef, copied = false, retrying = false, disabled = false, msg }) {
	const [expired, setExpired] = useState(() => isRetryExpired(sentAt));

	useEffect(() => {
		if (typeof sentAt !== "number") {
			setExpired(false);
			return undefined;
		}
		const left = RETRY_WINDOW_MS - (Date.now() - sentAt);
		if (left <= 0) {
			setExpired(true);
			return undefined;
		}
		setExpired(false);
		const timer = setTimeout(() => setExpired(true), left);
		return () => clearTimeout(timer);
	}, [sentAt]);

	const copyLabel = copied ? msg("chat.ref_copied", "Reference copied") : msg("chat.copy_ref", "Copy reference ID");
	return (
		<Box className="widget-send-failed" data-reference-id={referenceId}>
			<Typography component="span" className="widget-send-failed-text">
				{msg("chat.send_failed", "Message not sent")}
			</Typography>
			{!expired && (
				<Button
					size="small"
					className="widget-send-failed-retry"
					onClick={onRetry}
					disabled={retrying || disabled}
				>
					{msg("chat.retry", "Retry")}
				</Button>
			)}
			<Typography component="span" className="widget-send-failed-ref">{referenceId}</Typography>
			<Tooltip title={copyLabel}>
				<IconButton size="small" onClick={() => onCopyRef?.(referenceId)} sx={{ color: "var(--w-error)", p: 0.25 }}>
					<CopyIcon sx={{ fontSize: 13 }} />
				</IconButton>
			</Tooltip>
		</Box>
	);
}
